import { IHttpService, IPromise, IQService } from 'angular';
import DjangoClient from '../../tn.django/django-client.service/djangoClient';
import { IFoundryModel } from '../classes';
import {
    ICategory,
    ICategoryItem,
    ICategoryItemDict,
} from '../classes/Model/Category';
import { FilterCategoryName } from '../constants';
import { applyFilters, Filters } from '../functions';
import { buildCategory } from '../functions/categories';

function _transformDjangoFoundriesToCategoryItemDict(
    foundries: IFoundryModel[]
): ICategoryItemDict {
    return foundries.reduce<ICategoryItemDict>(
        (acc, foundry: IFoundryModel) => {
            const key = foundry.id.toString();
            const category: ICategoryItem = {
                id: foundry.id,
                name: foundry.name,
                slug: foundry.slug,
                ee_subdomain: foundry.ee_subdomain,
            };
            acc[key] = category;
            return acc;
        },
        {}
    );
}

export class FoundryService {
    djangoFoundriesPromise?: IPromise<IFoundryModel[]>;
    djangoFoundries: IFoundryModel[] = [];
    // quick lookup indexes
    djangoFoundriesIdxByDjanoId: Record<number, IFoundryModel> = {};
    djangoFoundriesIdxByEEDomain: Record<string, IFoundryModel> = {};
    djangoFoundriesIdxByDjangoSlug: Record<string, IFoundryModel> = {};

    static $inject = ['djangoClient', '$q', '$http'];
    constructor(
        protected djangoClient: DjangoClient,
        protected $q: IQService,
        protected $http: IHttpService
    ) {}

    protected loadDjangoFoundries(cached = true): IPromise<IFoundryModel[]> {
        if (this.djangoFoundriesPromise && cached) {
            return this.djangoFoundriesPromise;
        }
        this.djangoFoundriesPromise = this.djangoClient
            .getPaged<IFoundryModel>('/api/1/foundries/')
            .then((response) => {
                // map from django to frontend structure.
                const foundries = response.results.map((f) => {
                    return Object.assign({}, f, {
                        srefParams: { foundryEESubdomain: f.ee_subdomain },
                    });
                });
                this.djangoFoundries = foundries;
                this.djangoFoundries.forEach((foundry) => {
                    this.djangoFoundriesIdxByDjanoId[foundry.id] = foundry;
                    this.djangoFoundriesIdxByEEDomain[foundry.ee_subdomain] =
                        foundry;
                    this.djangoFoundriesIdxByDjangoSlug[foundry.slug] = foundry;
                });
                return this.djangoFoundries;
            })
            .catch((error) => {
                console.error('failed to load django foundries', error);
                return this.djangoFoundries;
            });
        return this.djangoFoundriesPromise;
    }

    filterDjangoFoundries(
        opts: Filters = new Filters(),
        cache = true
    ): IPromise<IFoundryModel[]> {
        const foundriesPromise = this.getDjangoFoundries(cache);
        return applyFilters(foundriesPromise, opts);
    }

    getDjangoFoundries(cache = true): IPromise<IFoundryModel[]> {
        return this.loadDjangoFoundries(cache).then(() => {
            return this.djangoFoundries;
        });
    }
    getDjangoFoundryByDjangoId(
        id: number,
        cache = true
    ): IPromise<IFoundryModel> {
        return this.loadDjangoFoundries(cache).then(() => {
            const foundry = this.djangoFoundriesIdxByDjanoId[id];
            // it seems like after login this ends up undefined. if the id we're looking for isn't in the cache, try to load it again.
            if (foundry != undefined) return foundry;
            return this.loadDjangoFoundries(false).then(() => {
                return this.djangoFoundriesIdxByDjanoId[id];
            });
        });
    }

    getDjangoFoundryBySubdomain(
        subdomain: string,
        cache = true
    ): IPromise<IFoundryModel> {
        return this.loadDjangoFoundries(cache).then(() => {
            return this.djangoFoundriesIdxByEEDomain[subdomain];
        });
    }

    getDjangoFoundryBySlug(
        slug: string,
        cache = true
    ): IPromise<IFoundryModel> {
        return this.loadDjangoFoundries(cache).then(() => {
            return this.getDjangoFoundryBySlug[slug];
        });
    }

    getDjangoFoundryCategory(cache = true): IPromise<ICategory> {
        return this.loadDjangoFoundries(cache).then((foundries) => {
            const filterCategoryName: FilterCategoryName = 'foundries';
            const categoryItemDict: ICategoryItemDict =
                _transformDjangoFoundriesToCategoryItemDict(foundries);
            return buildCategory(filterCategoryName, categoryItemDict);
        });
    }
}
